import {
  GraduationCap,
  CheckCircle2,
  Star,
  FileText,
  AlertTriangle,
  type LucideIcon,
} from "lucide-react";
import type { CoachingStats } from "../../api/coachingService";
import { PASS_SCORE } from "@/wecom-h5/coaching/scoreGrade";

export type SummaryCard = {
  /** i18n key suffix under `coaching:summary.*` */
  labelKey: string;
  value: string;
  icon: LucideIcon;
  color: string;
};

export type ScoreDistPoint = { range: string; count: number; color: string };

// V26 — 评分分布颜色用语义 token (success/info/warning/chart-5/destructive),
// 跨皮肤跟随主题切换。fill="hsl(var(--success))" 在 React 写入 SVG presentation
// attribute,现代浏览器(Chrome 49+/Safari 9.1+/FF 31+)支持 var() 解析。
export const SCORE_COLORS: Record<string, string> = {
  "90-100": "hsl(var(--success))",
  "80-89": "hsl(var(--info))",
  "70-79": "hsl(var(--warning))",
  "60-69": "hsl(var(--chart-5))",
  "<60": "hsl(var(--destructive))",
};

export function buildSummaryCards(stats: CoachingStats | undefined): SummaryCard[] {
  return [
    {
      labelKey: "todayEvaluations",
      value: stats ? String(stats.todayEvaluations) : "-",
      icon: GraduationCap,
      color: "bg-primary/10 text-primary",
    },
    {
      labelKey: "totalEvaluations",
      value: stats ? String(stats.totalEvaluations) : "-",
      icon: CheckCircle2,
      color: "bg-success/10 text-success",
    },
    {
      labelKey: "avgScore",
      value: stats ? String(stats.avgScore) : "-",
      icon: Star,
      color: "bg-warning/10 text-warning",
    },
    {
      labelKey: "pendingReportCount",
      value: stats ? String(stats.pendingReportCount) : "-",
      icon: FileText,
      color: "bg-info/10 text-info",
    },
    {
      labelKey: "riskEmployeeCount",
      value: stats ? String(stats.riskEmployeeCount) : "-",
      icon: AlertTriangle,
      color: "bg-destructive/10 text-destructive",
    },
  ];
}

export function buildScoreDistribution(stats: CoachingStats | undefined): ScoreDistPoint[] {
  if (!stats) return [];
  return (stats.scoreDistribution ?? []).map((d) => ({
    range: d.range,
    count: d.count,
    color: SCORE_COLORS[d.range] ?? "hsl(var(--info))",
  }));
}

/**
 * 分数配色:≥90 绿 / ≥80 主色 / ≥及格线 黄 / 以下红。及格线按任务自己的(passScore),不传 = 系统默认 60。
 * 此前三处各写死一份 90/80/70 —— 任务及格线设 60 时,69 分「及格了」却标红(1025 陪练舱录片实录)。
 */
export function scoreTone(score: number, passScore: number = PASS_SCORE) {
  if (score >= 90) return "text-success";
  if (score >= 80) return "text-primary";
  if (score >= passScore) return "text-warning";
  return "text-destructive";
}

export function gradeColor(grade: string | null) {
  if (grade === "优秀") return "text-success";
  if (grade === "良好") return "text-primary";
  if (grade === "合格") return "text-warning";
  return "text-destructive";
}

/**
 * 任务状态 → 展示文案。
 *
 * 后端存的是英文枚举(active / closed / archived),此前直接渲染出去,列表和抽屉里满屏
 * 「ACTIVE」—— 术语泄漏。i18n 里 tasksTab.status* 这几条文案一直都在,只是没接上。
 */
const TASK_STATUS_LABEL: Record<string, string> = {
  active: "进行中",
  closed: "已关闭",
  archived: "已归档",
  dispatching: "派发中",
};

export function taskStatusLabel(status: string | null | undefined): string {
  if (!status) return "—";
  return TASK_STATUS_LABEL[status.toLowerCase()] ?? status;
}

/**
 * badge 配色。★此前拿英文 status 去比中文字面量("已完成"/"进行中"),永远落到 else ——
 * 所有状态一个颜色,badge 等于没有信息量。改成认英文枚举。
 */
export function taskStatusType(status: string | null | undefined) {
  switch ((status ?? "").toLowerCase()) {
    case "closed":
      return "success";
    case "active":
      return "warning";
    default:
      return "info";
  }
}

export function pdfStatusType(s: string) {
  if (s === "ready" || s === "已生成") return "success";
  if (s === "生成中") return "warning";
  if (s === "生成失败") return "error";
  return "info";
}

/**
 * 后端返的是状态码不是中文。PDF 按需渲染(库里不存文件),有评估行就一定下得下来,
 * 所以 "ready" = 已生成 —— 此前后端恒返 "none",这里原样打出来,用户看到的是个英文单词。
 */
export function pdfStatusLabel(s: string) {
  if (s === "ready") return "已生成";
  if (s === "none") return "未生成";
  return s; // 老值(中文)原样透传
}

export function fmtDatetime(s: string | null) {
  if (!s) return "-";
  return s.replace("T", " ").substring(0, 16);
}

/**
 * 1005:对练场景难度。后端 / AI 生成回来的是英文枚举(EASY / MEDIUM / HARD,大小写不定),
 * 老数据里还有「初级 / 中级 / 高级」。此前场景卡直接渲染出 MEDIUM,编辑表单的 select
 * 选项值是小写,MEDIUM 对不上 → 下拉显示空。这里统一归一 + 出中文。
 */
export type Difficulty = "easy" | "medium" | "hard";

const DIFFICULTY_ALIASES: Record<string, Difficulty> = {
  easy: "easy",
  初级: "easy",
  简单: "easy",
  medium: "medium",
  中级: "medium",
  中等: "medium",
  hard: "hard",
  高级: "hard",
  困难: "hard",
};

export function normalizeDifficulty(d: string | null | undefined): Difficulty | null {
  if (!d) return null;
  return DIFFICULTY_ALIASES[d.trim().toLowerCase()] ?? null;
}

const DIFFICULTY_LABEL: Record<Difficulty, string> = { easy: "简单", medium: "中等", hard: "困难" };

export function difficultyLabel(d: string | null | undefined): string {
  const n = normalizeDifficulty(d);
  return n ? DIFFICULTY_LABEL[n] : (d ?? "—");
}

// 1022 试听:网关 CSP media-src 只放行 'self' blob: https:,data: URI 的音频会被浏览器直接拒播,
// 所以后端回的 base64 先转成 Blob,再用 blob: URL 播放。
export function base64ToBlob(base64: string, mime: string): Blob {
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

/**
 * 能派任务 / 员工能练 = 启用中(active,历史空值按启用)。草稿、已归档只在场景管理里出现 ——
 * 1025 实录:归档的行业包场景还在建任务下拉里(后端同口径 CoachingScenarioService.isPracticable)。
 */
export function isPracticableScenario(sc: { status?: string | null }): boolean {
  return !sc.status || sc.status.toLowerCase() === "active";
}
